import { ChartBarIcon, ViewListIcon } from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarSolidIcon,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import ConfigurationView from "./ConfigurationView";
import { useForLoop, useForLoopInputVariables } from "./hooks";
import { ForLoopConfiguration, LoopType } from "./types";

import { BlockProps, isEvalLikeEnv, useBlockEnv } from "@/components/PowerBlock/Block";
import { withBlockStore } from "@/components/PowerBlock/Block/block-store";
import BlockStatistics from "@/components/PowerBlock/Block/BlockStatistics";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { SourceType } from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import TabNavigation from "@/components/ui/TabNavigation";

const validationSchema = createBlockSchema<ForLoopConfiguration>()
  .requireName()
  .addRule(required("configuration.loop_type", "Loop type is required"))
  .addRule({
    path: "configuration.iterator_source",
    validate: (_value, fullPayload) => {
      const config = fullPayload.configuration;
      const hasIteratorSource = config?.iterator_source && config.iterator_source.trim() !== "";
      const hasMaxIterations =
        config?.max_iterations !== undefined && config?.max_iterations !== null;

      if (!hasIteratorSource && !hasMaxIterations) {
        return false;
      }
      if (hasIteratorSource && hasMaxIterations) {
        return false;
      }
      return true;
    },
    message: "Either iterator source or max iterations must be provided, but not both",
  })
  .addRule({
    path: "configuration.max_iterations",
    validate: (_value, fullPayload) => {
      const config = fullPayload.configuration;
      const hasIteratorSource = config?.iterator_source && config.iterator_source.trim() !== "";
      const hasMaxIterations =
        config?.max_iterations !== undefined && config?.max_iterations !== null;

      if (!hasIteratorSource && !hasMaxIterations) {
        return false;
      }
      if (hasIteratorSource && hasMaxIterations) {
        return false;
      }
      return true;
    },
    message: "Either iterator source or max iterations must be provided, but not both",
  })
  .addRule({
    path: "configuration.prompt_config.template.name",
    validate: (_value, fullPayload) => {
      if (fullPayload.configuration?.loop_type === LoopType.PROMPT) {
        // Valid if either registry template or inline template is configured
        const hasRegistryTemplate = !!fullPayload.configuration?.prompt_config?.template?.name;
        const hasInlineTemplate =
          !!fullPayload.configuration?.prompt_config?.inline_template?.prompt_template;
        return hasRegistryTemplate || hasInlineTemplate;
      }
      return true;
    },
    message: "Prompt template selection is required",
  })
  .addRule({
    path: "configuration.workflow_config.name",
    validate: (_value, fullPayload) => {
      if (fullPayload.configuration?.loop_type === LoopType.WORKFLOW) {
        return !!fullPayload.configuration?.workflow_config?.name;
      }
      return true;
    },
    message: "Workflow selection is required",
  })
  .build();

export const ForLoopBlockCore = observer(
  ({
    showVerboseMode = true,
    sources = createEmptySources(),
    id,
    readonly,
  }: Omit<BlockProps<ForLoopConfiguration>, "DefaultFields"> & {
    showVerboseMode?: boolean;
  }) => {
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const env = useBlockEnv();
    const isEval = isEvalLikeEnv(env);

    const {
      blockStore,
      configuration,
      loopType,
      setLoopType,
      setPromptConfig,
      setWorkflowConfig,
      setIteratorSource,
      setMaxIterations,
      setVariableMappings,
      setUseJsonPath,
    } = useForLoop();

    const handleTabChange = (tabId: number) => {
      setCurrentTabIndex(tabId);
    };

    const handleAutofill = useCallback(
      (mappings: Record<string, string>) => {
        setVariableMappings({
          ...configuration?.variable_mappings,
          ...mappings,
        });
      },
      [configuration?.variable_mappings, setVariableMappings],
    );

    const { inputVariables, isLoading: inputVariablesIsLoading } =
      useForLoopInputVariables(configuration);

    const enhancedSources = useMemo(
      () => ({
        ...sources,
        [SourceType.InputVariable]: [
          ...(sources[SourceType.InputVariable] || []),
          "loop_index",
          "previous_outputs",
          "_iterator_item",
        ],
      }),
      [sources],
    );

    const useIteratorSource =
      configuration?.iterator_source !== undefined && configuration?.max_iterations === undefined;

    const handleIteratorSourceChange = useCallback(
      (source: string | undefined) => {
        // When setting via JSON path input, ensure use_json_path is true
        const currentConfig = blockStore.configuration;
        blockStore.setConfiguration({
          ...currentConfig,
          loop_type: currentConfig?.loop_type || LoopType.PROMPT,
          iterator_source: source,
          use_json_path: true,
          max_iterations: undefined,
        });
      },
      [blockStore],
    );

    const handleUseJsonPathChange = useCallback(
      (enabled: boolean) => {
        if (enabled) {
          // When enabling JSON path mode, clear max iterations and ensure iterator_source is set
          setMaxIterations(undefined);
          if (!configuration?.iterator_source) {
            handleIteratorSourceChange("");
          }
        } else {
          // When disabling JSON path mode, clear the iterator_source if it was a JSON path
          // This prevents both JSON path and source selector values from being set
          if (configuration?.use_json_path && configuration?.iterator_source) {
            setIteratorSource(undefined);
          }
        }
        setUseJsonPath(enabled);
      },
      [
        configuration?.iterator_source,
        configuration?.use_json_path,
        setUseJsonPath,
        setMaxIterations,
        setIteratorSource,
        handleIteratorSourceChange,
      ],
    );

    const handleUseIteratorSourceChange = useCallback(
      (enabled: boolean) => {
        if (enabled) {
          setMaxIterations(undefined);
          if (!configuration?.iterator_source) {
            setIteratorSource("");
          }
        } else {
          const currentConfig = blockStore.configuration;
          blockStore.setConfiguration({
            ...currentConfig,
            loop_type: currentConfig?.loop_type || LoopType.PROMPT,
            use_json_path: false,
            iterator_source: undefined,
            max_iterations:
              currentConfig?.max_iterations === undefined ? 10 : currentConfig?.max_iterations,
          });
        }
      },
      [blockStore, setIteratorSource, setMaxIterations, configuration?.iterator_source],
    );

    const configurationContent = (
      <ConfigurationView
        loopType={loopType}
        iteratorSource={configuration?.iterator_source}
        maxIterations={configuration?.max_iterations}
        useIteratorSource={useIteratorSource}
        useJsonPath={configuration?.use_json_path}
        inputVariables={inputVariables}
        sources={enhancedSources}
        inputVariablesIsLoading={inputVariablesIsLoading}
        readonly={readonly}
        onLoopTypeChange={setLoopType}
        onIteratorSourceChange={handleIteratorSourceChange}
        onSourceSelectorChange={setIteratorSource}
        onMaxIterationsChange={setMaxIterations}
        onUseIteratorSourceChange={handleUseIteratorSourceChange}
        onUseJsonPathChange={handleUseJsonPathChange}
        onAutofill={handleAutofill}
        promptConfig={configuration?.prompt_config}
        onPromptConfigChange={setPromptConfig}
        showVerboseMode={showVerboseMode}
        workflowConfig={configuration?.workflow_config}
        onWorkflowConfigChange={setWorkflowConfig}
      />
    );

    return (
      <>
        {readonly && isEval ? (
          <TabNavigation
            selectedIndex={currentTabIndex}
            handleTabChange={handleTabChange}
            tabs={[
              {
                id: 0,
                name: "Overview",
                outlineIcon: <ViewListIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                solidIcon: <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                content: <div className="pt-2">{configurationContent}</div>,
              },
              {
                id: 1,
                name: "Statistics",
                outlineIcon: <ChartBarIcon className="h-4 w-4" />,
                solidIcon: <ChartBarSolidIcon className="h-4 w-4" />,
                content: <BlockStatistics id={id as number} />,
              },
            ]}
          />
        ) : (
          configurationContent
        )}
      </>
    );
  },
);

export default withBlockStore<ForLoopConfiguration>(ForLoopBlockCore, {
  validationSchema,
  defaultConfiguration: {
    loop_type: LoopType.PROMPT,
    max_iterations: 10,
  } as ForLoopConfiguration,
});
